/**
 * Project: Gus's Auto Dealership
 * Author: Eihab Khudhair
 * Description: Home page - Professional white theme with a redesigned hero that features a dedicated car image and dynamic content.
 */

'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { carsApi } from '@/lib/api';
import { Car } from '@/lib/types';
import Image from 'next/image';
import Head from 'next/head';
import {
  FaCar,
  FaShieldAlt,
  FaTag,
  FaStar,
  FaChevronRight,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaAward,
  FaHeart,
  FaArrowRight,
  FaCrown,
  FaChevronDown
  , FaRobot
} from 'react-icons/fa';
import { MdSpeed, MdLocalOffer, MdDirectionsCar } from 'react-icons/md';

export default function HomePage() {
  const [featuredCars, setFeaturedCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCars, setTotalCars] = useState<number | null>(null);
  const siteUrl = 'https://gussauto.com';
  const phone = '+16147720734';
  const sameAs: string[] = []; // add social links later if you want (Facebook/Instagram/etc)


  // Fetch featured cars and total available stock
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured cars (limit 4)
        const featuredResponse = await carsApi.getAll({ limit: 4, isAvailable: true });
        setFeaturedCars(featuredResponse.data.cars);

        // Fetch all available vehicles to compute stock count
        const countResponse = await carsApi.getAll({ isAvailable: true });
        // Some APIs return a meta.totalCount field, otherwise fall back to array length
        const totalCount =
          (countResponse.data?.meta?.totalCount as number | undefined) ??
          (Array.isArray(countResponse.data?.cars) ? countResponse.data.cars.length : 0);
        setTotalCars(totalCount);
      } catch (error) {
        console.error('Failed to fetch cars data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Dynamically compute stats array with actual stock count
  const stats = [
    {
      number: totalCars !== null ? totalCars.toString() : '—',
      label: 'Vehicles in Stock',
      icon: FaCar,
      color: 'text-blue-600'
    },
    {
      number: '98%',
      label: 'Customer Satisfaction',
      icon: FaStar,
      color: 'text-green-600'
    },
    {
      number: '15+',
      label: 'Years Experience',
      icon: FaAward,
      color: 'text-amber-600'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SEO: Structured Data (JSON-LD) */}
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              {
                '@context': 'https://schema.org',
                '@type': 'AutoDealer',
                name: "Gus's Auto Dealership",
                url: siteUrl,
                telephone: phone,
                image: `${siteUrl}/og.jpg`,
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: '1840 Atlas St',
                  addressLocality: 'Columbus',
                  addressRegion: 'OH',
                  postalCode: '43228',
                  addressCountry: 'US',
                },
                openingHoursSpecification: [
                  {
                    '@type': 'OpeningHoursSpecification',
                    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                    opens: '09:00',
                    closes: '18:00',
                  },
                ],
                sameAs,
              },
              {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: "Gus's Auto Dealership",
                url: siteUrl,
                potentialAction: {
                  '@type': 'SearchAction',
                  target: `${siteUrl}/inventory?search={search_term_string}`,
                  'query-input': 'required name=search_term_string',
                },
              },
            ]),
          }}
        />
      </Head>
      {/* Hero Section with car background and blue overlay to match inventory page */}
      <section className="relative min-h-[90vh] md:min-h-[95vh] overflow-hidden">
        {/* Background car image with blue gradient overlay */}
        <div className="absolute inset-0 z-0">
          {/* The car image should be placed in your public folder as /car.jpg */}
          <Image
            src="/car.jpg"
            alt="Luxury car"
            fill
            className="object-cover object-center"
            priority
          />
          {/* Blue overlay similar to the inventory page */}
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-700/60" />
        </div>

        {/* Premium badge in top left */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 z-20"
        >
          <div className="group relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-400 to-green-500 rounded-lg blur opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
            <div className="relative flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 bg-gradient-to-r from-emerald-50 to-green-50 backdrop-blur-sm border border-emerald-200 rounded-lg shadow-lg group-hover:border-emerald-300 group-hover:shadow-emerald-200/30 group-hover:scale-[1.02] transition-all duration-300">
              <div className="relative">
                <FaCrown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 group-hover:text-emerald-500 transition-colors" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full group-hover:animate-ping" />
              </div>
              <span className="text-xs sm:text-sm font-semibold text-emerald-700 group-hover:text-emerald-600 tracking-wide transition-colors">
                Premium Dealership Since 2008
              </span>
            </div>
          </div>
        </motion.div>

        {/* Main container: centralised text content and CTAs */}
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col items-center justify-center text-center py-10">
          <div className="max-w-3xl">
            {/* Heading and tagline */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight mb-4">
                <span className="block">Gus&apos;s Auto</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-emerald-500 to-blue-700">
                  Excellence
                </span>
              </h1>
              <p className="text-2xl md:text-3xl text-blue-200 mt-2 font-light">
                Where every drive becomes an experience
              </p>
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 mb-10 max-w-lg"
            >
              <p className="text-lg text-blue-200 leading-relaxed">
                Discover automotive perfection at Gus&apos;s Auto Dealership. We curate exceptional
                vehicles that combine luxury, performance, and reliability, ensuring your journey
                is nothing short of extraordinary.
              </p>
            </motion.div>

            {/* Call to action buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mb-12 justify-center"
            >
              <Link
                href="/inventory"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-emerald-500 rounded-lg group-hover:from-blue-500 group-hover:to-emerald-400 transition-all duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-emerald-400 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl" />
                <span className="relative flex items-center gap-3">
                  Explore Our Collection
                  <FaArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </span>
              </Link>
              <Link
                href="/contact"
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-blue-700 bg-white border-2 border-blue-200 rounded-lg hover:border-blue-400 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
                <span className="relative flex items-center gap-3">
                  <MdDirectionsCar className="w-6 h-6" />
                  Schedule Test Drive
                </span>
              </Link>
            </motion.div>

            {/* Quick stats beneath buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-blue-700/50 max-w-xl mx-auto"
            >
              {[{ value: '150+', label: 'Point Inspection' }, { value: '0%', label: 'Hidden Fees' }, { value: '5-Star', label: 'Customer Rating' }, { value: '98%', label: 'Customer Satisfaction' }].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-blue-100">
                    {stat.value}
                  </div>
                  <div className="text-sm text-blue-300 mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Removed right column graphic; background image now covers entire hero */}
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
        >
          <div className="flex flex-col items-center">
            <span className="text-gray-200 text-sm mb-3 font-medium">Discover More</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center"
            >
              <FaChevronDown className="w-4 h-4 text-gray-400 mt-2" />
            </motion.div>
          </div>
        </motion.div>

        {/* Animated robot at bottom of hero. The robot walks across the screen with a playful motion. */}
        <motion.div
          initial={{ x: '-10vw' }}
          animate={{ x: ['-10vw', '110vw'] }}
          transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          className="absolute bottom-6 left-0 z-20"
        >
          <FaRobot className="w-12 h-12 text-blue-300 drop-shadow-lg" />
        </motion.div>

        {/* Removed the floating contact card for a cleaner hero. */}
      </section>

      {/* Stats Section with updated car background */}
      <section className="relative py-16">
        {/* Background car image replaced with local image for consistency */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/car.jpg"
            alt="Background car"
            fill
            className="object-cover object-center"
          />
          {/* Light overlay to ensure readability */}
          <div className="absolute inset-0 bg-white/80" />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="text-center group bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${stat.color.replace(
                    'text-',
                    'bg-'
                  )}/10 border-2 ${stat.color.replace('text-', 'border-')}/20 mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-blue-600">Gus&apos;s Auto Dealership</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference with our commitment to excellence in every detail
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="group relative bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <FaShieldAlt className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4 text-center">Certified Quality</h3>
              <p className="text-gray-600 text-center mb-6">
                Every vehicle undergoes a 150-point inspection and comes with a detailed history report.
              </p>
              <div className="space-y-3">
                {['Full Inspection', 'Certified History', 'Quality Guarantee'].map((item, i) => (
                  <div key={i} className="flex items-center text-gray-700">
                    <FaCheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="group relative bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <FaTag className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4 text-center">Transparent Pricing</h3>
              <p className="text-gray-600 text-center mb-6">
                No hidden fees. We provide clear, competitive pricing with flexible financing options.
              </p>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Price Match</span>
                  <span className="text-green-600 font-semibold">Guaranteed</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-600">Financing Rates</span>
                  <span className="text-blue-600 font-semibold">From 2.9%</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="group relative bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                  <MdSpeed className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4 text-center">VIP Service</h3>
              <p className="text-gray-600 text-center mb-6">
                Experience our premium concierge service from test drive to delivery.
              </p>
              <div className="space-y-4">
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FaClock className="w-5 h-5 text-blue-500 mr-3" />
                  <span className="text-gray-700">Same-Day Test Drive</span>
                </div>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <FaUsers className="w-5 h-5 text-blue-500 mr-3" />
                  <span className="text-gray-700">Personal Sales Consultant</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="group relative bg-white p-8 rounded-xl border border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                  <MdLocalOffer className="w-8 h-8 text-white" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4 text-center">Trade-In Value</h3>
              <p className="text-gray-600 text-center mb-6">
                Get top dollar for your current vehicle with our competitive trade-in program.
              </p>
              <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                <div className="text-sm text-gray-600 mb-1">Average Trade-In Bonus</div>
                <div className="text-2xl font-bold text-green-600">+15%</div>
                <div className="text-sm text-gray-500">Above market value</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Cars Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-sm font-semibold mb-4">
              <FaStar className="mr-2" /> CURATED SELECTION
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Featured <span className="text-blue-600">Vehicles</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our handpicked collection of premium automobiles
            </p>
          </motion.div>
          {loading ? (
            <div className="flex justify-center items-center h-96">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <div className="absolute inset-0 border-4 border-blue-300 border-t-transparent rounded-full animate-spin animation-delay-500" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.map((car, index) => (
                <motion.div
                  key={car.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300"
                >
                  <div className="absolute top-4 right-4 z-10">
                    <div className="px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full text-xs font-semibold text-white shadow-md">
                      FEATURED
                    </div>
                  </div>
                  <div className="relative h-48 overflow-hidden">
                    {car.media[0] && (
                      <Image
                        src={car.media[0].url}
                        alt={`${car.year} ${car.make} ${car.model}`}
                        fill
                        className="object-cover group-hover:scale-110 transition-transform duration-500"
                        sizes="(max-width: 768px) 100vw, 25vw"
                        // Unoptimized prevents Next.js image loader domain restrictions
                        unoptimized
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                    <button
                      type="button"
                      aria-label="Add to favorites"
                      className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                    >
                      <FaHeart className="w-5 h-5 text-gray-600 hover:text-red-500" />
                    </button>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {car.year} {car.make} {car.model}
                        </h3>
                        <div className="flex items-center text-gray-600 text-sm">
                          <span className="flex items-center mr-4">
                            <MdSpeed className="w-4 h-4 mr-1" />{' '}
                            {car.mileage?.toLocaleString()} mi
                          </span>
                          <span>{car.transmission}</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-500 mb-1">Fuel Type</div>
                        <div className="text-sm font-medium text-gray-900">{car.fuelType}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-500 mb-1">Status</div>
                        <div className="text-sm font-medium text-green-600">Available</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="text-2xl font-bold text-blue-700">
                          ${car.price.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">Starting at</div>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                        Premium
                      </span>
                    </div>
                    {/* Only one action button: View Details. */}
                    <div className="flex">
                      <Link
                        href={`/inventory/${car.id}`}
                        className="flex-1 group/btn flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium py-3 rounded-lg transition-all duration-300 hover:shadow-lg"
                      >
                        View Details
                        <FaChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-16"
          >
            <Link
              href="/inventory"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 text-lg font-semibold text-blue-700 bg-white border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Explore Full Collection
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 group-hover:bg-blue-200 transition-colors">
                <FaChevronRight className="w-5 h-5" />
              </span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20 shadow-2xl"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Ready to Drive Excellence?
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Schedule a private viewing or test drive with one of our luxury vehicle specialists.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 text-lg font-semibold text-blue-900 bg-gradient-to-r from-cyan-300 via-blue-200 to-cyan-300 bg-size-200 bg-pos-0 hover:bg-pos-100 rounded-lg transition-all duration-500 shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                Book Consultation
                <FaArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link
                href="/financing"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 text-lg font-semibold text-white bg-transparent border-2 border-white/40 rounded-lg hover:bg-white/10 transition-all duration-300"
              >
                <FaTag className="w-5 h-5" />
                Financing Options
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}